/**
 * What a priority rating COSTS, and the assumptions the cost is divided by — edited here, on
 * **Planning → TA capacity planning**, at the bottom of the tab.
 *
 * ⚠️ IT MOVED OFF ROLE PRIORITISATION (Theresa, Sep 25 2026). Role Prioritisation's first sub-tab
 * is the Company View now: the page the rest of the business reads to find out what their open
 * roles are getting. What a rating costs TA is not that question — it is a TA decision, taken with
 * the roster and the trimester's target in front of you, which is this tab. The Company View shows
 * the RESULT (`ServiceLevels`, read-only) and nothing there can change it.
 *
 * Three folds, and each is either editable here or explicitly not:
 *
 *  · **Effort model and capacity assumptions** (`ModelEditor`) — editable, because both halves are
 *    the team's own estimate rather than a measurement, and every figure downstream is one divided
 *    by the other.
 *  · **Portfolio** — read-only by design. Role counts and ratings belong to Airtable; a scenario
 *    expresses a different portfolio as a delta rather than by typing over a count.
 *  · **Change log** (`ChangeLog`) — who moved which of the above. It replaced a "Reset working
 *    models" button, which answered the wrong question.
 *
 * ⚠️ THE TEAM FOLD IS GONE, not moved. It was a READ-ONLY copy of the shared roster pointing at
 * this very tab — and on this tab the roster itself is three sections up, editable. A read-only
 * mirror of a control directly above it is a second place to look and nothing to do there.
 *
 * ⚠️ The state is the cockpit's one prioritisation state (`RolePrioritisationProvider`), the same
 * INSTANCE the Company View and Balance the load read. Editing the model here moves every figure on
 * those views with no save step and no reload: there is one effort model, not one per tab.
 */
import { RATINGS } from '../../../types/rolePrioritisation';
import { hoursLabel, pctLabel, tierHours, tierLoad } from '../../../utils/rolePrioritisation';
import { Section } from '../tracking/trackingUi';
import { ChangeLog, LastChange } from './ChangeLog';
import { Fold, FoldList, Prose } from './CollapsedSection';
import { ModelEditor } from './ModelEditor';
import { RatingBadge } from './prioritisationUi';
import { useRolePrioritisationState } from './RolePrioritisationProvider';

const TH = 'border-b border-solid border-pewter px-3 py-2.5 text-right text-xs font-medium text-iron';
const TH_L = `${TH} text-left`;
const TD = 'border-b border-solid border-pearl px-3 py-2 text-right text-sm tabular-nums';
const TD_L = `${TD} text-left`;

export function ServiceLevelModel() {
  const { state, company: stats, model, applyState } = useRolePrioritisationState();

  return (
    <Section
      title="Service levels and effort model"
      aside={`${RATINGS.map((r) => `${r} ${hoursLabel(tierHours(model, r))} h`).join(' · ')} per role, per week`}
    >
      {/*
        Who last moved one of these, without opening the log. It sat beside the read-only team
        table before that fold was deleted — and it belongs to the model rather than to the team:
        the roster records its own `savedBy` three sections up.
      */}
      <div className="mt-2">
        <LastChange state={state} />
      </div>

      <FoldList>
        <ModelEditor state={state} stats={stats} onStateChange={applyState} />

        <Fold
          value="portfolio"
          title="Portfolio"
          hint={`${RATINGS.map((r) => `${r} ${stats.portfolio[r]}`).join(' · ')} · ${hoursLabel(stats.demand)} h`}
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className={TH_L}>Priority rating</th>
                  <th className={TH}>Hours per role</th>
                  <th className={TH}>Roles</th>
                  <th className={TH}>Weekly hours</th>
                  <th className={TH}>Share of load</th>
                  <th className={TH}>Capacity used</th>
                </tr>
              </thead>
              <tbody>
                {RATINGS.map((r) => {
                  const load = tierLoad(model, stats.portfolio, r);
                  return (
                    <tr key={r}>
                      <td className={TD_L}>
                        <RatingBadge rating={r} />
                      </td>
                      <td className={TD}>{hoursLabel(tierHours(model, r))} h</td>
                      <td className={TD}>{stats.portfolio[r]}</td>
                      <td className={`${TD} font-semibold`}>{hoursLabel(load)}</td>
                      <td className={TD}>
                        {pctLabel(stats.demand > 0 ? (load / stats.demand) * 100 : null)}
                      </td>
                      <td className={TD}>
                        {pctLabel(stats.committed > 0 ? (load / stats.committed) * 100 : null)}
                      </td>
                    </tr>
                  );
                })}
                <tr className="bg-cotton font-semibold tabular-nums">
                  <td className="px-3 py-2 text-left">Total</td>
                  <td className="px-3 py-2" />
                  <td className="px-3 py-2 text-right">{stats.openRoles}</td>
                  <td className="px-3 py-2 text-right">{hoursLabel(stats.demand)}</td>
                  <td className="px-3 py-2 text-right">100%</td>
                  <td className="px-3 py-2 text-right">{pctLabel(stats.utilisation)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <Prose>
            Role counts and ratings are never editable here — they belong to Airtable. A scenario
            expresses a different portfolio as a delta (&ldquo;+2 AAA&rdquo;) rather than by typing
            over a count, which is what keeps the baseline trustworthy when several people are
            exploring at once. Hours per role come from the effort model above, so editing that
            moves this table too.
          </Prose>
        </Fold>

        <ChangeLog state={state} />
      </FoldList>
    </Section>
  );
}
